<template>
    <Teleport to="body">
        <div
            v-if="active"
            ref="cardEl"
            class="cch"
            :style="{ left: pos.left + 'px', top: pos.top + 'px', visibility: placed ? 'visible' : 'hidden' }"
            @pointerenter="cancelClose"
            @pointerleave="scheduleClose"
        >
            <CContactSummary :key="activeId ?? undefined" :contact="active.contact" @navigate="closeContactHover" />
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import router from '@/router';

import { activeContactHover, cancelClose, closeContactHover, scheduleClose } from './contact-hover.directive';
import { ContactSummaries } from './contact-summary';
import { placeFloatingCard } from './contact-summary.helpers';

const CContactSummary = defineAsyncComponent(() => import('./c-contact-summary.vue'));

const active = activeContactHover;
const activeId = computed(() => (active.value ? (typeof active.value.contact === 'string' ? active.value.contact : active.value.contact.id) : null));
const cardEl = ref<HTMLElement>();
const pos = ref({ left: 0, top: 0 });
const placed = ref(false);

async function place() {
    await nextTick();
    const el = cardEl.value;
    const anchor = active.value?.anchorEl;
    if (!el || !anchor) return;
    const rect = el.getBoundingClientRect();
    pos.value = placeFloatingCard(
        anchor.getBoundingClientRect(),
        { width: rect.width, height: rect.height },
        { width: window.innerWidth, height: window.innerHeight }
    );
    placed.value = true;
}

// Re-place when the card's content changes size (data arrives, identity loads, async chunk lands).
watch(
    () => [
        activeId.value,
        active.value?.anchorEl,
        ContactSummaries.status(activeId.value),
        !!ContactSummaries.get(activeId.value),
        !!ContactSummaries.identity(activeId.value)
    ],
    () => {
        if (!active.value) return;
        // Chunk load for the async card can land after the first measure.
        place();
        setTimeout(place, 50);
    },
    { flush: 'post' }
);

watch(activeId, () => {
    placed.value = false;
});

// Content size changes (async chunk lands, identity loads) re-place the card.
let resizeObserver: ResizeObserver | null = null;
watch(cardEl, el => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    if (el && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => place());
        resizeObserver.observe(el);
    }
});

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') closeContactHover();
}
function onPointerDown(e: Event) {
    if (cardEl.value && e.target instanceof Node && cardEl.value.contains(e.target)) return;
    closeContactHover();
}
function onScroll(e: Event) {
    if (cardEl.value && e.target instanceof Node && cardEl.value.contains(e.target)) return;
    closeContactHover();
}

let removeAfterEach: (() => void) | null = null;

function attach() {
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', closeContactHover);
    window.addEventListener('keydown', onKeydown);
    document.addEventListener('pointerdown', onPointerDown, true);
    removeAfterEach = router.afterEach(() => closeContactHover());
}

function detach() {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', closeContactHover);
    window.removeEventListener('keydown', onKeydown);
    document.removeEventListener('pointerdown', onPointerDown, true);
    removeAfterEach?.();
    removeAfterEach = null;
}

watch(
    () => !!active.value,
    isOpen => {
        if (isOpen) {
            placed.value = false;
            attach();
        } else {
            detach();
        }
    }
);

onBeforeUnmount(() => {
    detach();
    resizeObserver?.disconnect();
});
</script>

<style scoped>
.cch {
    position: fixed;
    z-index: 10000;
}
</style>
